<template>
    <Modal
        :title="$t('protocol.title')"
        :is-open="isOpen"
        @update:is-open="setOpen($event)"
        :back-text="$t('nav.close')"
    >
        <div class="space-y-4">
            <HorseHeader :horse="horse" :displayOwners="false" read-only small />

            <div class="space-y-1">
                <h3> {{ protocol?.title }} </h3>
                <Paragraph> {{ protocol?.description }} </Paragraph>
            </div>

            <h4>
                {{
                    $t('metric.from-to', {
                        start: format(protocol?.start_at, 'D MMM YYYY'),
                        end: format(add(protocol?.start_at, toDayCount(protocol?.day_count) - 1, 'day'), 'D MMM YYYY'),
                    })
                }}
            </h4>

            <!-- I4: who prescribed the program and when the vet checks the horse. Never the reason nor the diagnostic. -->
            <Paragraph v-if="prescription">{{ prescription }}</Paragraph>

            <!-- P11: today's treatments first, then the whole list of the assignment. -->
            <ProgramTreatmentToday :treatments="todayTreatments" />

            <ProgramTreatments v-if="protocol?.treatments?.length" :treatments="protocol.treatments" />

            <Loading v-if="!steps" />
            <div v-else class="space-y-2">
                <div class="grid grid-cols-2 gap-3 md:grid-cols-3">
                    <ProtocolStepCard
                        v-for="(step, indexStep) of steps"
                        :key="`protocol-step-${indexStep}`"
                        :step="step"
                        :index="indexStep"
                        :editable="false"
                        :class="step?.isCurrent ? 'border-dark-lightest scale-102 border-2' : 'bg-dark-light/50'"
                    >
                        <template #top>
                            <div class="mt-1 flex text-xs">
                                {{ step?.range }}
                            </div>
                        </template>

                        <template v-if="step?.status" #icon-status>
                            <CheckIcon v-if="step?.status === 'done'" checked />
                        </template>
                    </ProtocolStepCard>
                </div>
            </div>
        </div>
    </Modal>
</template>

<script setup>
import { computed, ref, watch, onBeforeUnmount } from 'vue'
import { useI18n } from 'vue-i18n'

import { add, format, toLocaleDateString } from '@/lib/helpers/time'
import useProtocolSteps from '@/lib/hooks/use-protocol-steps'
import { toDayCount } from '@/lib/protocol-day-count'

import Paragraph from '@/components/Paragraph.vue'
import Modal from '@/components/modals/Modal.vue'
import ProtocolStepCard from '@/components/ProtocolStepCard.vue'
import HorseHeader from '@/components/HorseHeader.vue'
import Loading from '@/components/Loading.vue'
import CheckIcon from '@/components/icons/CheckIcon.vue'
import ProgramTreatments from '@/components/ProgramTreatments.vue'
import ProgramTreatmentToday from '@/components/ProgramTreatmentToday.vue'
import { programDayOn, treatmentsOnDay } from '@/lib/programs/treatments'
import { prescriptionLine } from '@/lib/programs/prescription'

const { t, locale } = useI18n()
const { setSteps } = useProtocolSteps()

const props = defineProps({
    isOpen: { type: Boolean, default: false },
    protocol: { type: Object, required: true },
    horse: { type: Object, required: true },
})

const emits = defineEmits(['update:isOpen'])

const steps = computed(() => setSteps(props.protocol?.steps, props.protocol?.start_at))

// Day 1 is the assignment's start: nothing before it, and after the program only
// the treatments that outlive it.
const todayTreatments = computed(() =>
    props.isOpen ? treatmentsOnDay(props.protocol?.treatments, programDayOn(props.protocol?.start_at, new Date())) : []
)

// "Prescribed by … · check on …", each half only when the API sent it; the vet
// check in the format the app gives it everywhere else (CrudProgramModal).
const prescription = computed(() =>
    prescriptionLine(props.protocol, {
        t,
        formatDate: (date) => toLocaleDateString(date, locale.value, { weekday: 'short', month: 'short' }),
    })
)

const setOpen = (state) => {
    emits('update:isOpen', state)
}
</script>
